import { z } from 'zod';
/**
 * Relatórios da clínica (mesa). Os do APOLO que cabem nos dados do Sec (agenda,
 * leads, tratamentos, financeiro), num formato só: resumo, um gráfico e a tabela,
 * já escritos como a recepção lê. A tabela é o que sai no CSV.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
export const CLINIC_REPORT_GROUPS = ['Agenda', 'Leads', 'Tratamentos', 'Financeiro'];
export const CLINIC_REPORTS = [
    {
        key: 'marcacoes',
        group: 'Agenda',
        title: 'Consultas marcadas',
        text: 'Quantas consultas foram marcadas no período (pela data em que se marcou) e por quem.',
    },
    {
        key: 'comparecimento',
        group: 'Agenda',
        title: 'Comparecimento',
        text: 'Das consultas do período: quem veio, quem faltou, o que foi cancelado, por tipo.',
    },
    {
        key: 'horarios',
        group: 'Agenda',
        title: 'Horários mais cheios',
        text: 'Em que horas do dia e dias da semana as consultas se concentram.',
    },
    {
        key: 'origens',
        group: 'Leads',
        title: 'Leads por origem',
        text: 'Quem chegou no período, de onde veio e quantos viraram paciente.',
    },
    {
        key: 'funil',
        group: 'Leads',
        title: 'Funil de leads',
        text: 'Onde estão os cartões agora, os prazos vencidos, vendas e perdas do período.',
    },
    {
        key: 'perdas',
        group: 'Leads',
        title: 'Perdas por motivo',
        text: 'Por que os leads do período não seguiram.',
    },
    {
        key: 'tratamentos',
        group: 'Tratamentos',
        title: 'Tratamentos iniciados',
        text: 'Os tratamentos que começaram no período, como estão hoje e o valor combinado.',
    },
    {
        key: 'medicamentos',
        group: 'Tratamentos',
        title: 'Medicamentos',
        text: 'Os pedidos do período por situação, o tempo do pedido até a entrega e o custo por tratamento.',
    },
    {
        key: 'recebido',
        group: 'Financeiro',
        title: 'Recebido',
        text: 'O que entrou no período, por semana e por forma de pagamento.',
    },
];
export const CLINIC_REPORT_KEYS = CLINIC_REPORTS.map((row) => row.key);
export const clinicReportKeySchema = z.enum(CLINIC_REPORT_KEYS);
/** Relatórios que só quem cuida do financeiro vê. */
export const CLINIC_FINANCE_REPORTS = new Set(['recebido']);
/** Período máximo de um relatório, em dias. */
export const CLINIC_REPORT_MAX_DAYS = 400;
export const clinicReportQuerySchema = z.object({ from: isoDate, to: isoDate });
export const clinicReportSchema = z.object({
    key: clinicReportKeySchema,
    title: z.string(),
    from: isoDate,
    to: isoDate,
    /** Os números do alto: rótulo e valor já escrito ("72%", "R$ 4.300,00"). */
    summary: z.array(z.object({ label: z.string(), value: z.string(), hint: z.string().nullable() })),
    chart: z
        .object({
        /** Colunas para o tempo (semana, hora); barras deitadas para categorias. */
        kind: z.enum(['colunas', 'barras']),
        title: z.string(),
        items: z.array(z.object({
            label: z.string(),
            value: z.number().min(0),
            /** Cor de situação (veio, faltou, esperando); sem ela, a cor da clínica. */
            tone: z.enum(['ok', 'bad', 'wait', 'mute']).optional(),
        })),
        /** Como escrever o valor do item ("12", "R$ 300"). */
        unit: z.enum(['numero', 'dinheiro', 'porcento']),
    })
        .nullable(),
    table: z.object({
        columns: z.array(z.string()),
        rows: z.array(z.array(z.string())),
    }),
    /** Uma linha sobre como a conta é feita, quando importa. */
    note: z.string().nullable(),
});
